import { context, trace, SpanStatusCode, type Span } from '@opentelemetry/api';
import { getTracer } from './telemetry';

/**
 * Run an async action within a click-named span so that any fetches the action
 * triggers become children of the click. The `context.with(...)` wrap is
 * load-bearing — without it, mutations queued on a microtask escape the span
 * context and the resulting fetch span becomes orphan (CLAUDE.md: load-bearing
 * trace structure regression test in telemetry.test.ts).
 *
 * Pattern (from PLAN-v2 §A5 brief):
 *
 *   onClick={() => withClickSpan('add-to-cart', { 'app.product.id': p.id }, () =>
 *     mutation.mutateAsync({ productId: p.id }),
 *   )}
 */
export async function withClickSpan<T>(
  buttonId: string,
  attrs: Record<string, string | number | boolean>,
  action: () => Promise<T>,
): Promise<T> {
  const tracer = getTracer('frontend.ui');
  const span: Span = tracer.startSpan('app.button.click', {
    attributes: { 'app.button.id': buttonId, ...attrs },
  });
  const ctx = trace.setSpan(context.active(), span);
  try {
    return await context.with(ctx, action);
  } catch (err) {
    span.recordException(err as Error);
    span.setStatus({ code: SpanStatusCode.ERROR });
    throw err;
  } finally {
    span.end();
  }
}
